import {
  ComboboxContent,
  ComboboxControl,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemText,
  ComboboxPositioner,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxIndicatorGroup,
  useFilter,
  useListCollection,
} from '@chakra-ui/react';
import React from 'react';

type ModelItem = { label: string; value: string };

export interface AiModelComboboxProps {
  value: string;
  onValueChange: (id: string) => void;
  options: string[];
  disabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

export function AiModelCombobox({
  value,
  onValueChange,
  options,
  disabled,
  size = 'sm',
}: AiModelComboboxProps) {
  const items = React.useMemo<ModelItem[]>(
    () => options.map((id) => ({ label: id, value: id })),
    [options],
  );

  const { contains } = useFilter({ sensitivity: 'base' });
  const { collection, filter, set } = useListCollection<ModelItem>({
    initialItems: items,
    filter: contains,
  });

  React.useEffect(() => {
    set(items);
  }, [items, set]);

  const handleValueChange = React.useCallback(
    (details: { value: string[] }) => {
      const next = details.value[0];
      if (next != null) onValueChange(next);
    },
    [onValueChange],
  );

  return (
    <ComboboxRoot
      collection={collection}
      value={[value]}
      onValueChange={handleValueChange}
      onInputValueChange={(e) => filter(e.inputValue)}
      disabled={disabled}
      size={size}
      w="100%"
      openOnClick
      allowCustomValue={false}
      positioning={{ strategy: 'fixed', hideWhenDetached: true }}
    >
      <ComboboxControl>
        <ComboboxInput placeholder="Search models…" />
        <ComboboxIndicatorGroup>
          <ComboboxTrigger />
        </ComboboxIndicatorGroup>
      </ComboboxControl>
      <ComboboxPositioner>
        <ComboboxContent>
          <ComboboxEmpty>No matching models</ComboboxEmpty>
          {collection.items.map((item) => (
            <ComboboxItem key={item.value} item={item}>
              <ComboboxItemText>{item.label}</ComboboxItemText>
            </ComboboxItem>
          ))}
        </ComboboxContent>
      </ComboboxPositioner>
    </ComboboxRoot>
  );
}
